/**
 * ADAPTIVE RESOURCE TIER: Local self-regulation for transfer parameters.
 *
 * Each peer computes its own resource tier at startup from locally available
 * browser APIs. The tier determines transfer concurrency, window depth, and
 * timeout parameters — preventing resource-constrained peers (Firefox, low-RAM
 * ChromeOS) from entering oversaturation → channel collapse → reconnect loops
 * while leaving powerful Chromium peers at full speed.
 *
 * Tier 1 (constrained):  Firefox on any system, or Chromium with ≤4 GB RAM
 * Tier 2 (moderate):     Chromium with 4–8 GB RAM
 * Tier 3 (powerful):     Chromium with ≥8 GB RAM and ≥4 cores
 */

export interface TransferResourceProfile {
  tier: 1 | 2 | 3;
  label: string;

  // --- Receiver-side (pull window) ---
  /** Max chunks requested from a single source per fillGroupPullWindow call */
  maxNewRequestsPerSource: number;
  /** Global pipeline budget across all active receiving transfers */
  totalPipelineBudget: number;
  /** Max decoded-but-not-flushed chunks before stalling new requests */
  writeBackpressureLimit: number;
  /** Stale timeout (ms) — how long a 0-symbol request sits before eviction */
  pullStaleMs: number;
  /** Absolute max (ms) — force-evict even with partial symbols */
  pullStuckMaxMs: number;

  // --- Sender-side (serve caps) ---
  /** Max concurrent chunk serves to a single peer */
  maxConcurrentServesPerPeer: number;
  /** Total serve pipeline budget across all targets */
  servePipelineBudget: number;
  /** Max symbols per burst in group serve — higher = fewer event loop yields = faster */
  groupSymbolBurstMax: number;

  // --- Diagnostics ---
  browser: 'firefox' | 'chromium' | 'safari' | 'unknown';
  deviceMemoryGB: number | null;
  hardwareConcurrency: number;
  hasPerformanceMemory: boolean;
}

/** Compact capability byte for Layer 2 peer exchange (future use) */
export interface ResourceCapabilityByte {
  /** 2-bit browser class: 0=Firefox, 1=Chromium, 2=Safari, 3=other */
  browserClass: number;
  /** 3-bit RAM tier: 0=unknown, 1=≤2GB, 2=4GB, 3=8GB, 4=≥16GB */
  ramTier: number;
  /** 3-bit core count bucket: 0=unknown, 1=1-2, 2=3-4, 3=5-8, 4=≥9 */
  coreTier: number;
}

// ─── Browser Detection ─────────────────────────────────────────────

function detectBrowser(): 'firefox' | 'chromium' | 'safari' | 'unknown' {
  if (typeof navigator === 'undefined') return 'unknown';
  const ua = navigator.userAgent;
  // Firefox must be checked before Chrome (FF UA contains "Gecko" but not "Chrome")
  if (/Firefox\//.test(ua)) return 'firefox';
  // Safari must be checked before Chrome (Safari UA on iOS contains "Safari" but not "Chrome")
  if (/Safari\//.test(ua) && !/Chrome\//.test(ua) && !/Chromium\//.test(ua)) return 'safari';
  // Chromium-based: Chrome, Edge, Brave, Opera, Arc, etc.
  if (/Chrome\/|Chromium\/|Edg\//.test(ua)) return 'chromium';
  return 'unknown';
}

// ─── Hardware Detection ────────────────────────────────────────────

function getDeviceMemoryGB(): number | null {
  // navigator.deviceMemory is a Chromium-only API (not Firefox/Safari).
  // Returns approximate RAM in GB: 0.25, 0.5, 1, 2, 4, 8.
  // Capped at 8 for fingerprinting protection (≥8 all report 8).
  if (typeof navigator !== 'undefined' && 'deviceMemory' in navigator) {
    return (navigator as any).deviceMemory as number;
  }
  return null;
}

function getHardwareConcurrency(): number {
  if (typeof navigator !== 'undefined' && 'hardwareConcurrency' in navigator) {
    return navigator.hardwareConcurrency || 2;
  }
  return 2; // conservative fallback
}

function hasPerformanceMemoryAPI(): boolean {
  return typeof performance !== 'undefined' && 'memory' in performance;
}

// ─── Tier Computation ──────────────────────────────────────────────

function computeTier(
  browser: 'firefox' | 'chromium' | 'safari' | 'unknown',
  deviceMemoryGB: number | null,
  cores: number,
  hasPerfMemory: boolean
): 1 | 2 | 3 {
  // Firefox: Always Tier 1.
  // No performance.memory → no real heap visibility.
  // OPFS circuit-breaker trips regularly.
  // WebRTC SCTP runs on main thread (not separate thread like Chromium).
  // Event loop contention causes ICE keepalive starvation → false disconnects.
  if (browser === 'firefox') return 1;

  // Safari: Start at Tier 3 (powerful). Safari has solid OPFS (createSyncAccessHandle
  // in workers), functional RTCDataChannel, and no SCTP-on-main-thread issue like
  // Firefox. Apple hardware is generally well-specced. Missing navigator.deviceMemory
  // and performance.memory means we rely on runtime downgrade (via transfer stall
  // detection and OPFS backpressure) to protect lower-end iPads/iPhones. Prefer
  // starting fast and downgrading on pressure over penalizing M-series Macs upfront.
  // With hardwareConcurrency ≥ 8 → likely M1+ (8 perf+efficiency cores).
  // With hardwareConcurrency < 8 → older device, start at Tier 2.
  if (browser === 'safari') return cores >= 8 ? 3 : 2;

  // Unknown browser: conservative Tier 1.
  if (browser === 'unknown') return 1;

  // Chromium: tier based on available RAM and cores.
  // deviceMemory is null when the API is blocked by anti-fingerprinting
  // (Brave, hardened Chrome, privacy extensions, iframes).
  if (deviceMemoryGB === null) {
    // Brave-safe fallback: if performance.memory is available, trust it and
    // assume Tier 2. Otherwise, fall back to hardwareConcurrency, which is
    // still exposed even when deviceMemory/performance.memory are hidden.
    //   cores >= 8 → desktop-class → Tier 3
    //   cores >= 4 → laptop-class   → Tier 2
    //   cores < 4  → low-end        → Tier 1
    if (hasPerfMemory) return 2;
    if (cores >= 8) return 3;
    if (cores >= 4) return 2;
    return 1;
  }

  // Low RAM (≤2 GB): Chromebooks, cheap tablets, old laptops
  if (deviceMemoryGB <= 2) return 1;

  // Moderate RAM (4 GB) or few cores: most Chromebooks, entry laptops
  if (deviceMemoryGB <= 4 || cores <= 2) return 2;

  // High RAM (≥8 GB) + decent cores: desktop-class
  if (deviceMemoryGB >= 8 && cores >= 4) return 3;

  // Default moderate
  return 2;
}

// ─── Profile Generation ────────────────────────────────────────────

function buildProfile(tier: 1 | 2 | 3, browser: string, deviceMemoryGB: number | null, cores: number, hasPerfMemory: boolean): TransferResourceProfile {
  // Tier 1: Constrained (Firefox, low-RAM Chromium, Safari)
  // Prioritize stability over speed. Serialize everything.
  if (tier === 1) {
    return {
      tier: 1,
      label: 'constrained',
      maxNewRequestsPerSource: 1,
      totalPipelineBudget: 2,
      writeBackpressureLimit: 2,  // FIX B: was 1 — serialized everything. 2 allows overlap of OPFS write + next decode
      pullStaleMs: 45_000,     // 45s — very generous for slow channels
      pullStuckMaxMs: 90_000,  // 90s absolute max
      maxConcurrentServesPerPeer: 1,
      servePipelineBudget: 2,
      groupSymbolBurstMax: 50,   // FIX A: was capped at 20 globally — 50 for constrained peers
      browser: browser as any,
      deviceMemoryGB,
      hardwareConcurrency: cores,
      hasPerformanceMemory: hasPerfMemory,
    };
  }

  // Tier 2: Moderate (Chromium 4–8 GB RAM)
  // Balanced concurrency. Standard timeouts.
  if (tier === 2) {
    return {
      tier: 2,
      label: 'moderate',
      maxNewRequestsPerSource: 1,
      totalPipelineBudget: 3,
      writeBackpressureLimit: 2,
      pullStaleMs: 30_000,     // 30s
      pullStuckMaxMs: 60_000,  // 60s
      maxConcurrentServesPerPeer: 1,
      servePipelineBudget: 3,
      groupSymbolBurstMax: 100,  // FIX A: moderate burst for balanced peers
      browser: browser as any,
      deviceMemoryGB,
      hardwareConcurrency: cores,
      hasPerformanceMemory: hasPerfMemory,
    };
  }

  // Tier 3: Powerful (Chromium ≥8 GB RAM, ≥4 cores)
  // Full concurrency. Tighter timeouts to recycle stale requests faster.
  return {
    tier: 3,
    label: 'powerful',
    maxNewRequestsPerSource: 2,
    totalPipelineBudget: 4,
    writeBackpressureLimit: 3,
    pullStaleMs: 20_000,     // 20s — fast eviction on powerful hardware
    pullStuckMaxMs: 45_000,  // 45s
    maxConcurrentServesPerPeer: 2,
    servePipelineBudget: 4,
    groupSymbolBurstMax: 200,  // FIX A: aggressive burst — approach P2P throughput
    browser: browser as any,
    deviceMemoryGB,
    hardwareConcurrency: cores,
    hasPerformanceMemory: hasPerfMemory,
  };
}

// ─── Capability Byte (Layer 2 — future peer exchange) ──────────────

export function encodeCapabilityByte(profile: TransferResourceProfile): number {
  const browserClass = profile.browser === 'firefox' ? 0
    : profile.browser === 'chromium' ? 1
    : profile.browser === 'safari' ? 2
    : 3;

  const ramTier = profile.deviceMemoryGB === null ? 0
    : profile.deviceMemoryGB <= 2 ? 1
    : profile.deviceMemoryGB <= 4 ? 2
    : profile.deviceMemoryGB <= 8 ? 3
    : 4;

  const coreTier = profile.hardwareConcurrency <= 0 ? 0
    : profile.hardwareConcurrency <= 2 ? 1
    : profile.hardwareConcurrency <= 4 ? 2
    : profile.hardwareConcurrency <= 8 ? 3
    : 4;

  // Pack: [browser:2][ram:3][cores:3]
  return ((browserClass & 0x3) << 6) | ((ramTier & 0x7) << 3) | (coreTier & 0x7);
}

export function decodeCapabilityByte(byte: number): ResourceCapabilityByte {
  return {
    browserClass: (byte >> 6) & 0x3,
    ramTier: (byte >> 3) & 0x7,
    coreTier: byte & 0x7,
  };
}

// ─── Pressure Tracking + Runtime Tier Adjustment ──────────────────
//
// Start at the best tier the hardware supports (baseTier). Downgrade
// when real-time pressure signals exceed thresholds. Upgrade back
// toward baseTier when pressure clears. This avoids penalizing fast
// systems upfront while protecting constrained peers adaptively.

interface PressureEvent {
  type: 'backpressure' | 'stale_eviction' | 'memory_critical' | 'opfs_critical';
  timestamp: number;
}

// Tuning constants
const PRESSURE_WINDOW_MS   = 60_000;   // rolling window for pressure events
const DOWNGRADE_COOLDOWN_MS = 90_000;  // min time between downgrades
const UPGRADE_COOLDOWN_MS   = 60_000;  // min time after last pressure event before upgrade
const STALE_EVICTION_THRESHOLD  = 3;   // zero-symbol evictions in window → downgrade
const BACKPRESSURE_HIT_THRESHOLD = 5;  // OPFS write stalls in window → downgrade
const MEMORY_CRITICAL_THRESHOLD  = 2;  // memory monitor critical polls → downgrade
const OPFS_CRITICAL_THRESHOLD    = 2;  // OPFS critical polls → downgrade

let pressureEvents: PressureEvent[] = [];
let lastTierChangeTime = 0;

// ─── Singleton ─────────────────────────────────────────────────────

let cachedProfile: TransferResourceProfile | null = null;
let baseTier: 1 | 2 | 3 = 2;       // hardware-determined ceiling
let effectiveTier: 1 | 2 | 3 = 2;   // current runtime tier (may be lower)
let initialized = false;

// Cached hardware detection results (computed once, reused on rebuild)
let _browser: ReturnType<typeof detectBrowser> = 'unknown';
let _deviceMemoryGB: number | null = null;
let _cores = 2;
let _hasPerfMemory = false;

function ensureInitialized(): void {
  if (initialized) return;
  _browser = detectBrowser();
  _deviceMemoryGB = getDeviceMemoryGB();
  _cores = getHardwareConcurrency();
  _hasPerfMemory = hasPerformanceMemoryAPI();
  baseTier = computeTier(_browser, _deviceMemoryGB, _cores, _hasPerfMemory);
  effectiveTier = baseTier;
  initialized = true;
}

function rebuildProfile(): void {
  cachedProfile = buildProfile(effectiveTier, _browser, _deviceMemoryGB, _cores, _hasPerfMemory);
}

/**
 * Get the local peer's transfer resource profile.
 * Computed once at first call and cached. Updated in-place when
 * pressure tracking triggers a tier change.
 */
export function getTransferResourceProfile(): TransferResourceProfile {
  ensureInitialized();
  if (!cachedProfile) {
    rebuildProfile();
    console.log(`[RESOURCE_TIER] Initial profile`, {
      tier: cachedProfile!.tier,
      label: cachedProfile!.label,
      browser: _browser,
      deviceMemoryGB: _deviceMemoryGB,
      cores: _cores,
      hasPerfMemory: _hasPerfMemory,
      pipelineBudget: cachedProfile!.totalPipelineBudget,
      maxServesPerPeer: cachedProfile!.maxConcurrentServesPerPeer,
      pullStaleMs: cachedProfile!.pullStaleMs,
    });
  }
  return cachedProfile!;
}

/**
 * Force full reset (e.g., testing). Clears all state including pressure history.
 */
export function resetTransferResourceProfile(): void {
  cachedProfile = null;
  initialized = false;
  pressureEvents = [];
  lastTierChangeTime = 0;
}

// ─── Pressure Reporting API ────────────────────────────────────────
// Called from hot paths in provider.tsx and MemoryMonitor callbacks.

function pruneOldEvents(): void {
  const cutoff = Date.now() - PRESSURE_WINDOW_MS;
  pressureEvents = pressureEvents.filter(e => e.timestamp > cutoff);
}

function countByType(type: PressureEvent['type']): number {
  return pressureEvents.filter(e => e.type === type).length;
}

function evaluateDowngrade(): void {
  ensureInitialized();
  if (effectiveTier <= 1) return; // floor
  const now = Date.now();
  if (now - lastTierChangeTime < DOWNGRADE_COOLDOWN_MS) return;

  pruneOldEvents();
  const stale = countByType('stale_eviction');
  const bp = countByType('backpressure');
  const mem = countByType('memory_critical');
  const opfs = countByType('opfs_critical');

  const shouldDowngrade =
    stale >= STALE_EVICTION_THRESHOLD ||
    bp >= BACKPRESSURE_HIT_THRESHOLD ||
    mem >= MEMORY_CRITICAL_THRESHOLD ||
    opfs >= OPFS_CRITICAL_THRESHOLD;

  if (!shouldDowngrade) return;

  const oldTier = effectiveTier;
  effectiveTier = (effectiveTier - 1) as 1 | 2 | 3;
  lastTierChangeTime = now;
  rebuildProfile();
  console.warn(`[RESOURCE_TIER] ⬇ Downgrade ${oldTier}→${effectiveTier}`, {
    staleEvictions: stale,
    backpressureHits: bp,
    memoryCritical: mem,
    opfsCritical: opfs,
    newLabel: cachedProfile!.label,
    pipelineBudget: cachedProfile!.totalPipelineBudget,
  });
}

function evaluateUpgrade(): void {
  ensureInitialized();
  if (effectiveTier >= baseTier) return; // ceiling
  const now = Date.now();
  if (now - lastTierChangeTime < UPGRADE_COOLDOWN_MS) return;

  pruneOldEvents();
  if (pressureEvents.length > 0) return; // still under pressure

  const oldTier = effectiveTier;
  effectiveTier = (effectiveTier + 1) as 1 | 2 | 3;
  lastTierChangeTime = now;
  rebuildProfile();
  console.log(`[RESOURCE_TIER] ⬆ Upgrade ${oldTier}→${effectiveTier} (no pressure in ${PRESSURE_WINDOW_MS / 1000}s)`, {
    baseTier,
    newLabel: cachedProfile!.label,
    pipelineBudget: cachedProfile!.totalPipelineBudget,
  });
}

/**
 * Report an OPFS write backpressure stall (decoded chunks waiting to flush
 * exceeded the limit). Called from fillGroupPullWindow when the write queue
 * is saturated.
 */
export function reportBackpressureHit(): void {
  pressureEvents.push({ type: 'backpressure', timestamp: Date.now() });
  evaluateDowngrade();
}

/**
 * Report a zero-symbol stale eviction (chunk sat in the pull window for
 * GROUP_PULL_STALE_MS with 0 symbols received). Indicates the source
 * couldn't serve in time — likely oversaturation.
 */
export function reportStaleEviction(): void {
  pressureEvents.push({ type: 'stale_eviction', timestamp: Date.now() });
  evaluateDowngrade();
}

/**
 * Report memory pressure from MemoryMonitor (JS heap or OPFS critical).
 * Called from MemoryMonitor subscription callback.
 */
export function reportMemoryPressure(type: 'memory_critical' | 'opfs_critical'): void {
  pressureEvents.push({ type, timestamp: Date.now() });
  evaluateDowngrade();
}

/**
 * Check if conditions have improved enough to upgrade back toward baseTier.
 * Called periodically (e.g., from MemoryMonitor poll when levels are normal,
 * or from multisource sweep loop).
 */
export function checkPressureRelief(): void {
  evaluateUpgrade();
}

/**
 * Get current pressure diagnostics (for logging/debugging).
 */
export function getPressureDiagnostics(): {
  baseTier: number;
  effectiveTier: number;
  recentEvents: number;
  staleEvictions: number;
  backpressureHits: number;
  memoryCritical: number;
  opfsCritical: number;
  msSinceLastTierChange: number;
} {
  pruneOldEvents();
  return {
    baseTier,
    effectiveTier,
    recentEvents: pressureEvents.length,
    staleEvictions: countByType('stale_eviction'),
    backpressureHits: countByType('backpressure'),
    memoryCritical: countByType('memory_critical'),
    opfsCritical: countByType('opfs_critical'),
    msSinceLastTierChange: lastTierChangeTime > 0 ? Date.now() - lastTierChangeTime : -1,
  };
}
